import { useQuery, useQueries } from "react-query"
import qs from "query-string"

import { apiRequest } from "util/request"

/** ------------------- **/
/*  Shopify / Merchant
/** ------------------- **/

export function useGetMerchantSales({ groupId, startDate, endDate }) {
  const cacheKey = ["merchantSales", groupId, startDate, endDate]
  const paramString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`group/${groupId}/sales?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && !!startDate && !!endDate,
  })
}

export function useGetMerchantProductDetails({ groupId, productId }) {
  const cacheKey = ["merchantProductDetails", groupId, productId]
  const query = () => apiRequest(`group/${groupId}/${productId}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && !!productId,
  })
}

export function useGetMerchantProductSales({
  productId,
  groupId,
  startDate,
  endDate,
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = [
    "merchantProductSales",
    groupId,
    productId,
    startDate,
    endDate,
  ]
  const query = () =>
    apiRequest(`group/${groupId}/${productId}/sales?${paramsString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && !!productId,
  })
}

export function useMultipleGroupProductDetails({
  competitorProductAndCompanyIds,
}) {
  const useQueriesArray =
    competitorProductAndCompanyIds?.map(productAndCompanyIds => ({
      queryKey: [
        "merchantProductDetails",
        productAndCompanyIds[0],
        productAndCompanyIds[1],
      ],
      queryFn: () =>
        apiRequest(
          `group/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}`
        ),
    })) ?? []
  return useQueries({ queries: useQueriesArray })
}

export function useMultipleGroupProductSales({
  groupProductAndCompanyIds,
  startDate,
  endDate,
}) {
  const useQueriesArray =
    groupProductAndCompanyIds?.map(productAndCompanyIds => {
      const paramsString = qs.stringify({
        start_date: startDate,
        end_date: endDate,
      })
      return {
        queryKey: [
          "merchantProductSales",
          productAndCompanyIds[0],
          productAndCompanyIds[1],
          startDate,
          endDate,
        ],
        queryFn: () =>
          apiRequest(
            `group/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}/sales?${paramsString}`
          ),
      }
    }) ?? []
  return useQueries({ queries: useQueriesArray })
}

export function useSearchGroupProducts({
  companyId,
  debouncedSearchTerm,
  pageSize,
  pageOffset,
}) {
  const paramString = qs.stringify({
    title: debouncedSearchTerm,
    group_id: companyId,
    page_size: pageSize,
    page_number: pageOffset,
  })
  const cacheKey = [
    "searchMerchantProducts",
    companyId,
    debouncedSearchTerm,
    pageSize,
    pageOffset,
  ]
  const query = () => apiRequest(`group/merchant_product_search?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId && debouncedSearchTerm?.length > 2,
  })
}

export function useGetLtvCac({ groupId, todaysDate }) {
  const cacheKey = ["ltvCac", groupId, todaysDate]
  const paramString = qs.stringify({
    date: todaysDate,
  })
  const query = () =>
    apiRequest(`group/${groupId}/ltv_and_cac_stats?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && !!todaysDate,
  })
}

export function useVariantInventoryTimeseries({
  groupId,
  productId,
  variantId,
  startDate,
  endDate,
}) {
  const paramString = qs.stringify({
    group_id: groupId,
    product_id: productId,
    variant_id: variantId,
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = [
    "variantInventoryTimeseries",
    groupId,
    productId,
    variantId,
    startDate,
    endDate,
  ]
  const query = () =>
    apiRequest(`group/${groupId}/inventory_timeseries?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId,
  })
}
